<template>
  <div class="gs-checkout-builder-step gs-checkout-config-step">
    <section class="gs-checkout-config-step__section">
      <div class="gs-checkout-config-step__section-copy">
        <h3>Checkout identity</h3>
        <p>Use a clear internal name so your team can distinguish this checkout from other product links.</p>
      </div>
      <div class="gs-checkout-config-step__field">
        <label>{{ $t('frontend_name') }}</label>
        <text-input
          v-model="localModel.name"
          class="w-full"
          :placeholder="$t('frontend_name')"
          :validator="$v.localModel.name"
        />
        <small>Between 3 and 80 characters.</small>
      </div>
    </section>

    <section class="gs-checkout-config-step__section">
      <div class="gs-checkout-config-step__switch-row">
        <div>
          <h3>{{ $t('frontend_product_step1_for_specific_aff') }}</h3>
          <p>Turn this on only when the checkout should always credit one affiliate.</p>
        </div>
        <gd-toggle
          v-model="localModel.force_affiliate"
          :value="localModel.force_affiliate"
          :aria-label="$t('frontend_product_step1_for_specific_aff')"
        />
      </div>

      <div v-if="localModel.force_affiliate" class="gs-checkout-config-step__affiliate">
        <label>{{ $t('frontend_affiliate') }}</label>
        <auto-suggest-drop-down
          v-if="funnel_id"
          :funnel="funnel_id"
          :excluded="excludedAffIdArr"
          :affiliate_ids="selectedAffIdArr"
          :title="$t('frontend_affiliate')"
          @onAffiliateAdded="addedAffiliate_add_team"
          @removedAffiliate_add_team="removedAffiliate_add_team"
        />
        <div v-if="affiliate_err" class="gs-checkout-config-step__error" role="alert">
          {{ $t('frontend_aff_required') }}
        </div>
      </div>
    </section>

    <section v-if="hasFreePricePoints" class="gs-checkout-config-step__section">
      <div class="gs-checkout-config-step__section-copy">
        <h3>Checkout offer type</h3>
        <p>Choose whether this checkout presents paid or free price points.</p>
      </div>
      <div class="gs-checkout-config-step__choices">
        <div v-for="checkoutType in checkoutTypeFreePaid" :key="checkoutType.id">
          <gd-custom-radio
            v-model="selCheckoutType"
            :option="checkoutType"
            @onUpdateOption="changeType"
          />
        </div>
      </div>
    </section>

    <popup-alert v-if="alertProps.show" :variant="alertProps.variant" @close="alertProps.show = false">
      <template v-slot:title>
        {{ alertProps.title }}
      </template>
      <p>{{ alertProps.message }}</p>
    </popup-alert>
  </div>
</template>

<script>
import AutoSuggestDropDown from '@/components/AutoSuggestDropDown';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';

export default {
  name: 'ConfigStep',

  components: {
    AutoSuggestDropDown,
  },

  props: {
    model: {
      required: true,
    },
    product: {
      required: true,
    },
    selectedCheckoutStepType: {
      required: true,
    },
    selectedMultiStepTypeProp: {
      required: true,
    },
    selectedCheckoutTypeFreePaid: {
      required: true,
    },
  },

  data() {
    return {
      alertProps: {
        show: false,
        variant: 'success',
        title: '',
        message: '',
      },
      selectedMultiStepType: this.selectedMultiStepTypeProp,
      localModel: this.model,
      checkoutFlow: 1,
      selectedCheckoutFlow: 0,
      selCheckoutStepType: this.selectedCheckoutStepType,
      checkoutTypeFreePaid: [
        {
          id: 1,
          title: this.$t('frontend_product_step1_type_free_paid1_title'),
          description: this.$t('frontend_product_step1_type_free_paid1_desc'),
        },
        {
          id: 2,
          title: this.$t('frontend_product_step1_type_free_paid2_title'),
          description: this.$t('frontend_product_step1_type_free_paid2_desc'),
        },
      ],
      checkoutStepTypes: [
        {
          id: 1,
          title: this.$t('forntend_checkout_link_step_1'),
          description: this.$t('forntend_checkout_link_step_1_desc'),
          // coming_soon: true,
        },
        {
          id: 0,
          title: this.$t('forntend_checkout_link_multistep_checkout'),
          description: this.$t('forntend_checkout_link_multistep_checkout_desc'),
        },
      ],

      selCheckoutType: this.selectedCheckoutTypeFreePaid,

      multiStepTypes: [
        {
          id: 3,
          svg: ['Email', 'OrderForm_V2'],
          label: this.$t('frontend_checkout_links_step2_multiStepTypes3'),
        },
        {
          id: 2,
          svg: ['Email', 'NameAndBilling', 'Payment'],
          label: this.$t('frontend_checkout_links_step2_multiStepTypes2'),
        },
        {
          id: 1,
          svg: ['Contact', 'Billing', 'Payment'],
          label: this.$t('frontend_checkout_links_step2_multiStepTypes1'),
        },
      ],
      singleStepTypes: [
        {
          id: 1,
          svg: ['OrderForm_V2'],
          label: 'Order Form',
        },
      ],
      affiliateSettingsTypes: [
        {
          id: 0,
          title: this.$t('frontend_for_all_aff'),
          description: this.$t('frontend_for_all_aff_desc'),
        },
        {
          id: 1,
          title: this.$t('frontend_for_single_aff'),
          description: this.$t('frontend_for_single_desc'),
        },
      ],
      funnel_id: this.$route.params.id,
      selectedAffIdArr: [],
      excludedAffIdArr: [],
      affiliate_err: false,
    };
  },

  validations: {
    localModel: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(80),
      },
    },
  },

  watch: {
    localModel() {
      this.$emit('updateModel', this.localModel);
    },

    selCheckoutStepType() {
      this.$emit('updateSelCheckoutStepType', this.selCheckoutStepType);
    },

    selectedMultiStepType() {
      this.$emit('updateselectedMultiStepType', this.selectedMultiStepType);
    },
  },

  computed: {
    hasFreePricePoints() {
      if (
        this.product &&
        this.product.price_points &&
        this.product.price_points.length > 0
      ) {
        return this.product.price_points.some((el) => {
          return el.price == 0;
        });
      }
      return false;
    },
    productHasOnlyFreePricePoints() {
      if (
        this.product &&
        this.product.price_points &&
        this.product.price_points.length > 0
      ) {
        return this.product.price_points.every((el) => {
          return el.price == 0;
        });
      }
      return false;
    },
  },

  async created() {
    this.selectedMultiStepType = this.selectedMultiStepTypeProp;
    this.localModel.checkout_link_page_options = { ...this.localModel.checkout_link_page_options };
    this.checkoutFlow = 'Single Price Checkout';
    this.selectedCheckoutFlow = 0;

    if (!this.localModel.force_affiliate) {
      this.localModel.force_affiliate = 0;
    }
    if (this.model.force_affiliate_id) {
      this.selectedAffIdArr.push(this.model.force_affiliate_id);
      this.excludedAffIdArr.push(this.model.force_affiliate_id);
    }
    if (this.productHasOnlyFreePricePoints) {
      this.checkoutTypeFreePaid = [
        {
          id: 1,
          title: this.$t('frontend_product_step1_type_free_paid1_title'),
          description: this.$t('frontend_product_step1_type_free_paid1_desc'),
          coming_soon: true,
          disabled_message: this.$t('frontend_product_step1_type_free_paid1_disabled_message'),
        },
        {
          id: 2,
          title: this.$t('frontend_product_step1_type_free_paid2_title'),
          description: this.$t('frontend_product_step1_type_free_paid2_desc'),
        }
      ];
    }
  },

  methods: {
    changeType(value) {
      this.$emit('updateselectedCheckoutTypeFreePaid', value);
    },

    validate() {
      this.$v.$touch();
      let isValid = !this.$v.$invalid;
      this.affiliate_err = false;

      if (this.localModel.force_affiliate && this.localModel.force_affiliate_id == null) {
        isValid = false;
        this.alertProps = {
          show: true,
          variant: 'error',
          title: 'Oops...',
          message: this.$t('frontend_aff_required'),
        };
      }

      this.$emit('on-validate', isValid);
      return isValid;
    },

    addedAffiliate_add_team(affiliate) {
      this.localModel.force_affiliate_id = affiliate.id;
      this.localModel.force_affiliate = 1;
      this.selectedAffIdArr = [];
      this.excludedAffIdArr = [];
      this.selectedAffIdArr.push(affiliate.id);
      this.excludedAffIdArr.push(affiliate.id);
      this.affiliate_err = false;
    },

    removedAffiliate_add_team(affiliate) {
      this.localModel.force_affiliate_id = null;
      this.selectedAffIdArr.splice(this.selectedAffIdArr.indexOf(affiliate), 1);
      this.excludedAffIdArr.splice(this.excludedAffIdArr.indexOf(affiliate), 1);
    },
  },
};
</script>

<style scoped>
.gs-checkout-config-step {
  display: grid;
  gap: 18px;
}

.gs-checkout-config-step__section {
  padding: 20px;
  background: #fff;
  border: 1px solid var(--gs-border, #e2e8f0);
  border-radius: 12px;
}

.gs-checkout-config-step__section-copy {
  margin-bottom: 16px;
}

.gs-checkout-config-step h3,
.gs-checkout-config-step p {
  margin: 0;
}

.gs-checkout-config-step h3,
.gs-checkout-config-step__field > label,
.gs-checkout-config-step__affiliate > label {
  color: var(--gs-foreground, #182230);
  font-size: 14px;
  font-weight: 800;
}

.gs-checkout-config-step p,
.gs-checkout-config-step__field small {
  color: var(--gs-muted-foreground, #667085);
  font-size: 12px;
  line-height: 1.5;
}

.gs-checkout-config-step__section-copy p,
.gs-checkout-config-step__switch-row p {
  margin-top: 4px;
}

.gs-checkout-config-step__field {
  max-width: 620px;
}

.gs-checkout-config-step__field > label,
.gs-checkout-config-step__affiliate > label {
  display: block;
  margin-bottom: 7px;
}

.gs-checkout-config-step__field small {
  display: block;
  margin-top: 6px;
}

.gs-checkout-config-step__switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.gs-checkout-config-step__affiliate {
  max-width: 620px;
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid var(--gs-border, #e2e8f0);
}

.gs-checkout-config-step__error {
  margin-top: 8px;
  color: var(--gs-danger, #dc2626);
  font-size: 12px;
  font-weight: 700;
}

.gs-checkout-config-step__choices {
  display: grid;
  max-width: 680px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.gs-checkout-config-step__choices > div {
  min-width: 0;
}
</style>
